import React, { useEffect, useMemo, useRef, useState } from 'react';
import GameLayout from './GameLayout';
import './CatchFallingFoodGame.css';

import partyBomb from './partyBomb';

const FOODS = ['🍕', '🍟', '🍔', '🍩', '🍣', '🍓', '🍪'];

function clamp(v, a, b) {
  return Math.max(a, Math.min(b, v));
}

export default function CatchFallingFoodGame() {
  const areaRef = useRef(null);
  const rafRef = useRef(null);
  const lastRef = useRef(0);

  const [running, setRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [basketX, setBasketX] = useState(50); // %
  const [items, setItems] = useState([]); // {id, x, y, speed, emoji}

  const spawn = () => {
    setItems((arr) => {
      const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      return [
        ...arr,
        {
          id,
          x: Math.random() * 90 + 5,
          y: -10,
          speed: 18 + Math.random() * 22,
          emoji: FOODS[Math.floor(Math.random() * FOODS.length)],
        },
      ];
    });
  };

  const start = () => {
    setScore(0);
    setTimeLeft(20);
    setBasketX(50);
    setItems([]);
    setRunning(true);
  };

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;

    const onMove = (clientX) => {
      const rect = el.getBoundingClientRect();
      const pct = ((clientX - rect.left) / rect.width) * 100;
      setBasketX(clamp(pct, 8, 92));
    };

    const onTouch = (e) => {
      if (!running) return;
      const t = e.touches[0];
      if (t) onMove(t.clientX);
    };

    const onMouse = (e) => {
      if (!running) return;
      onMove(e.clientX);
    };

    el.addEventListener('touchstart', onTouch, { passive: true });
    el.addEventListener('touchmove', onTouch, { passive: true });
    el.addEventListener('mousemove', onMouse);

    return () => {
      el.removeEventListener('touchstart', onTouch);
      el.removeEventListener('touchmove', onTouch);
      el.removeEventListener('mousemove', onMouse);
    };
  }, [running]);

  useEffect(() => {
    if (!running) return;

    let spawnTimer = 0;
    lastRef.current = performance.now();

    const tick = (now) => {
      const dt = Math.min(0.05, (now - lastRef.current) / 1000);
      lastRef.current = now;

      spawnTimer += dt;
      if (spawnTimer > 0.75) {
        spawnTimer = 0;
        spawn();
      }

      setItems((arr) =>
        arr
          .map((it) => ({ ...it, y: it.y + it.speed * dt }))
          .filter((it) => it.y < 115)
      );

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [running]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => {
    if (!running) return;
    if (timeLeft <= 0) {
      setRunning(false);
      // Celebrate a completed round
      partyBomb({ particleCount: 90 });
      return;
    }

    const basketLeft = basketX - 10;
    const basketRight = basketX + 10;

    setItems((arr) => {
      let caught = 0;
      const next = arr.filter((it) => {
        const inBasket = it.y >= 86 && it.y <= 98 && it.x >= basketLeft && it.x <= basketRight;
        if (inBasket) caught += 1;
        return !inBasket;
      });
      if (caught) setScore((s) => s + caught);
      return next;
    });
  }, [basketX, timeLeft, running]);

  const headerRight = useMemo(
    () => (
      <div className="cff-stats">
        <span>⏱ {Math.max(0, timeLeft)}s</span>
        <span>⭐ {score}</span>
      </div>
    ),
    [score, timeLeft]
  );

  return (
    <GameLayout title="Catch the Falling Food" subtitle="20 seconds. Drag to catch!" rightSlot={headerRight}>
      <div className="cff-wrap">
        <div ref={areaRef} className="cff-area" aria-label="Game area">
          {items.map((it) => (
            <div
              key={it.id}
              className="cff-item"
              style={{ left: `${it.x}%`, top: `${it.y}%` }}
              aria-hidden="true"
            >
              {it.emoji}
            </div>
          ))}

          <div className="cff-basket" style={{ left: `${basketX}%` }} aria-hidden="true">
            🧺
          </div>

          {!running ? (
            <div className="cff-overlay">
              <div className="cff-card">
                <div className="cff-title">{timeLeft <= 0 ? 'Time!' : 'Ready?'}</div>
                <div className="cff-sub">Catch as many foods as you can.</div>
                <button className="cff-btn" onClick={start}>
                  {timeLeft <= 0 ? 'Play again' : 'Start'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </GameLayout>
  );
}
